import axios from 'axios';
import Cookies from 'js-cookie';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';
console.log('🌐 [DEBUG] API Base URL:', API_BASE_URL);

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,  // Increased to 60s for bank workflow processing (OpenAI assistants take 40-50s)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and handle FormData
apiClient.interceptors.request.use(
  (config) => {
    const token = Cookies.get('sankalvax_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If data is FormData, remove Content-Type header to let browser set it with boundary
    const isFormData = config.data instanceof FormData ||
                       (config.data && config.data.constructor && config.data.constructor.name === 'FormData');
    if (isFormData) {
      delete config.headers['Content-Type'];
      // Ensure axios doesn't transform FormData
      config.transformRequest = [(data) => data];
      console.log('📎 FormData detected - removing Content-Type header for multipart upload');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ [DEBUG] API Response success:', response.config.url, response.status);
    return response.data;
  },
  (error) => {
    console.error('❌ [DEBUG] API Error:', error.config?.url, error.response?.status, error.message);
    
    // Only redirect to login on 401 if it's NOT a login or register attempt
    if (error.response?.status === 401 &&
        !error.config?.url?.includes('/api/auth/login') &&
        !error.config?.url?.includes('/api/auth/register')) {
      console.log('🔐 [DEBUG] 401 error - clearing auth and redirecting to login');
      // Token expired or invalid
      Cookies.remove('sankalvax_token');
      Cookies.remove('sankalvax_user');
      window.location.href = '/login';
    }
    
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (credentials) => {
    return await apiClient.post('/api/auth/login', credentials);
  },

  register: async (userData) => {
    return await apiClient.post('/api/auth/register', userData);
  },

  logout: async (token) => {
    return await apiClient.post('/api/auth/logout', { token });
  },

  verifyToken: async (token) => {
    return await apiClient.get('/api/auth/me');
  },

  getCurrentUser: async () => {
    return await apiClient.get('/api/auth/me');
  },
};

// Connector API
export const connectorAPI = {
  getConnectors: async () => {
    return await apiClient.get('/api/connectors');
  },

  createConnector: async (connectorData) => {
    // Map service type to auth type (fallback if not provided)
    const authTypeMapping = {
      'netsuite': 'oauth1',
      'openai': 'api_key',
      'anthropic': 'api_key',
      'salesforce': 'client_credentials',
      'microsoft': 'client_credentials'
    };

    // Use the credentials endpoint to store connector credentials
    const credentialPayload = {
      connector_name: connectorData.name,
      // Use provided auth_type if available, otherwise use mapping
      auth_type: connectorData.auth_type || authTypeMapping[connectorData.service_type] || 'oauth1',
      credentials: connectorData.credentials,
      instance_name: connectorData.instance_name || 'default',
      metadata: {
        service_type: connectorData.service_type,
        auth_method: connectorData.auth_type,
        created_via: 'connector_form',
        ...connectorData.metadata
      }
    };
    return await apiClient.post('/api/connectors/credentials', credentialPayload);
  },

  updateConnector: async (connectorName, connectorData) => {
    // Map service type to auth type (fallback if not provided)
    const authTypeMapping = {
      'netsuite': 'oauth1',
      'openai': 'api_key',
      'anthropic': 'api_key',
      'salesforce': 'client_credentials',
      'microsoft': 'client_credentials'
    };

    const credentialPayload = {
      connector_name: connectorData.name,
      // Use provided auth_type if available, otherwise use mapping
      auth_type: connectorData.auth_type || authTypeMapping[connectorData.service_type] || 'oauth1',
      credentials: connectorData.credentials,
      instance_name: connectorData.instance_name || 'default',
      metadata: {
        service_type: connectorData.service_type,
        auth_method: connectorData.auth_type,
        updated_via: 'connector_form',
        ...connectorData.metadata
      }
    };
    return await apiClient.put(`/api/connectors/credentials/${connectorName}`, credentialPayload);
  },

  deleteConnector: async (connectorName) => {
    return await apiClient.delete(`/api/connectors/credentials/${connectorName}`);
  },

  testConnection: async (connectorName) => {
    return await apiClient.post(`/api/connectors/test/${connectorName}`);
  },

  getConnectorTypes: async () => {
    return await apiClient.get('/api/connectors/types');
  },

  // Get user's configured connectors
  getUserConnectors: async () => {
    return await apiClient.get('/api/connectors/credentials');
  },

  // Get individual connector details with credentials
  getConnector: async (connectorName) => {
    return await apiClient.get(`/api/connectors/credentials/${connectorName}`);
  },

  // Get all instances of a specific connector type
  getConnectorInstances: async (connectorName) => {
    return await apiClient.get(`/api/connectors/credentials/${connectorName}/instances`);
  },

  // Set which instance is the active/selected one
  setSelectedInstance: async (connectorName, instanceName) => {
    return await apiClient.post('/api/connectors/credentials/select-instance', {
      connector_name: connectorName,
      instance_name: instanceName
    });
  },

  // Deselect/deactivate a connector instance
  deselectInstance: async (connectorName, instanceName) => {
    return await apiClient.post('/api/connectors/credentials/deselect-instance', {
      connector_name: connectorName,
      instance_name: instanceName
    });
  },

  // Delete a specific connector instance
  deleteConnectorInstance: async (connectorName, instanceName) => {
    return await apiClient.delete(`/api/connectors/credentials/${connectorName}/instances/${instanceName}`);
  },
};

// Agent API
export const agentAPI = {
  getAgents: async () => {
    return await apiClient.get('/api/agents');
  },

  createAgent: async (agentData) => {
    return await apiClient.post('/api/agents', agentData);
  },

  updateAgent: async (agentId, agentData) => {
    return await apiClient.put(`/api/agents/${agentId}`, agentData);
  },

  deleteAgent: async (agentId) => {
    return await apiClient.delete(`/api/agents/${agentId}`);
  },

  getAgent: async (agentId) => {
    return await apiClient.get(`/api/agents/${agentId}`);
  },

  executeAgent: async (agentId, payload) => {
    return await apiClient.post(`/api/agents/${agentId}/execute`, payload);
  },
};

// Discovery API
export const discoveryAPI = {
  getDiscoveredAgents: async () => {
    return await apiClient.get('/api/agents');
  },

  refreshDiscovery: async () => {
    return await apiClient.post('/api/agents/refresh');
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    return await apiClient.get('/api/system/info');
  },

  getRecentActivity: async () => {
    return await apiClient.get('/api/agents');
  },

  getSystemStatus: async () => {
    return await apiClient.get('/api/health');
  },
};

// Workflow API
export const workflowAPI = {
  saveWorkflow: async (workflowData) => {
    return await apiClient.post('/api/workflow/save-from-frontend', workflowData);
  },

  getWorkflows: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return await apiClient.get(`/api/workflow/?${queryParams}`);
  },

  getWorkflow: async (workflowId) => {
    return await apiClient.get(`/api/workflow/${workflowId}`);
  },

  executeWorkflow: async (workflowId, inputData = {}) => {
    return await apiClient.post('/api/workflow/execute', {
      workflow_id: workflowId,
      input_data: inputData
    });
  },

  getExecutions: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return await apiClient.get(`/api/workflow/executions?${queryParams}`);
  },

  getExecution: async (executionId) => {
    return await apiClient.get(`/api/workflow/execution/${executionId}`);
  },

  // YAML Workflow Methods
  validateYaml: async (yamlData) => {
    return await apiClient.post('/api/workflow/validate-yaml', yamlData);
  },

  createFromYaml: async (yamlData) => {
    return await apiClient.post('/api/workflow/create-from-yaml', yamlData);
  },

  updateWorkflow: async (workflowId, workflowData) => {
    return await apiClient.put(`/api/workflow/${workflowId}`, workflowData);
  },

  exportYaml: async (workflowId) => {
    return await apiClient.get(`/api/workflow/${workflowId}/export-yaml`);
  },

  // V2 Workflow Methods
  getCurrentState: async (workflowId, runId = null) => {
    const params = runId ? `?run_id=${runId}` : '';
    // Use agent-specific routes: workflow 16 = Bank, workflow 15 = Sale Order, workflow 21 = SF Sale Order, workflow 28 = Budget, others = default
    let baseRoute = '/api/v2/workflows';  // Default for Bill Processing and others
    if (workflowId === 16) {
      baseRoute = '/api/bank/v2/workflows';
    } else if (workflowId === 15) {
      baseRoute = '/api/sale/workflows';
    } else if (workflowId === 21) {
      baseRoute = '/api/sale-sf/workflows';
    } else if (workflowId === 28 || workflowId === '28') {
      baseRoute = '/api/budget/v2/workflows';
    }
    // Note: Workflow 22 (Bill Processing WS) uses V2 routes for current-state but WebSocket engine for execution
    return await apiClient.get(`${baseRoute}/${workflowId}/current-state${params}`);
  },

  startWorkflowV2: async (workflowId, inputData, token) => {
    // Use agent-specific routes: workflow 16 = Bank, workflow 15 = Sale Order, workflow 21 = SF Sale Order, workflow 28 = Budget, others = default
    // Note: Workflow 22 uses V2 endpoint but backend routes to WebSocket engine
    let endpoint = '/api/v2/workflows/start';  // Default for Bill Processing and others
    if (workflowId === 16) {
      endpoint = '/api/bank/v2/workflows/start';
    } else if (workflowId === 15) {
      endpoint = '/api/sale/workflows/start';
    } else if (workflowId === 21) {
      endpoint = '/api/sale-sf/workflows/start';
    } else if (workflowId === 28 || workflowId === '28') {
      endpoint = '/api/budget/v2/workflows/start';
    }
    return await apiClient.post(endpoint, {
      workflow_id: workflowId,
      user_message: inputData,
      token: token
    });
  },

  continueWorkflowV2: async (runId, inputData) => {
    // TODO: Need to determine if this is a bank workflow run
    // For now, using default route - may need to check run_id in database
    return await apiClient.post(`/api/v2/workflows/continue/${runId}`, {
      input_data: inputData
    });
  },

  submitUIForm: async (runId, formData, token, workflowId = null) => {
    // Check if formData contains any File objects
    const hasFiles = Object.values(formData).some(value => value instanceof File);

    // Determine endpoint and timeout based on workflow type
    let endpoint;
    let timeout = 60000; // Default 60s timeout

    if (workflowId === 16) {
      // Bank Agent workflow - needs longer timeout for AI processing (OpenAI takes 2+ minutes)
      endpoint = `/api/bank/v2/workflows/ui-submit/${runId}`;
      timeout = 600000; // 10 minutes for bank workflow AI steps
    } else if (workflowId === 15) {
      // Sale Order workflow (NetSuite)
      endpoint = `/api/sale/workflows/ui-submit/${runId}`;
      timeout = 180000; // 3 minutes for sale order workflow
    } else if (workflowId === 21) {
      // SF Sale Order workflow (Salesforce)
      endpoint = `/api/sale-sf/workflows/ui-submit/${runId}`;
      timeout = 180000; // 3 minutes for SF sale order workflow
    } else if (workflowId === 28 || workflowId === '28') {
      // Budget Agent workflow (check both number and string since it may come from URL params)
      endpoint = `/api/budget/v2/workflows/ui-submit/${runId}`;
      timeout = 180000; // 3 minutes for budget workflow
    } else {
      // Default (Bill Processing and others)
      endpoint = `/api/v2/workflows/ui-submit/${runId}`;
      timeout = 180000; // 3 minutes for bill processing
    }

    if (hasFiles) {
      // Use multipart/form-data for file uploads
      const submitData = new FormData();

      // Add token to form data
      submitData.append('token', token);

      // Create form data without file objects for JSON serialization
      const formDataWithoutFiles = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (!(value instanceof File)) {
          formDataWithoutFiles[key] = value;
        }
      });

      // Add form data as JSON string
      submitData.append('form_data', JSON.stringify(formDataWithoutFiles));

      // Add files with their original field names (backend expects this)
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value instanceof File) {
          submitData.append(key, value);
          console.log(`📎 Adding file with field name '${key}': ${value.name} (${value.size} bytes)`);
        }
      });

      console.log('📎 Submitting with files via multipart/form-data');

      // Debug: Log all FormData entries
      console.log('📊 FormData contents:');
      for (let [key, value] of submitData.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }

      // Submit with multipart/form-data (don't set Content-Type header, let browser set it)
      return await apiClient.post(endpoint, submitData, {
        timeout: timeout,
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let browser set multipart boundary
        }
      });
    } else {
      // Use JSON for forms without files
      console.log(`📝 Submitting without files via JSON (timeout: ${timeout/1000}s)`);
      return await apiClient.post(endpoint, {
        form_data: formData,
        token: token
      }, {
        timeout: timeout
      });
    }
  },

  saveDraftWorkflow: async (workflowId, runId, draftData, token) => {
    return await apiClient.post('/api/chat/save-draft', {
      workflow_id: workflowId,
      run_id: runId,
      draft_data: draftData,
      token: token
    });
  }
};

// Generic API helper
export const apiHelper = {
  get: async (endpoint) => {
    return await apiClient.get(endpoint);
  },

  post: async (endpoint, data) => {
    return await apiClient.post(endpoint, data);
  },

  put: async (endpoint, data) => {
    return await apiClient.put(endpoint, data);
  },

  delete: async (endpoint) => {
    return await apiClient.delete(endpoint);
  },
};

// Prompts API
export const promptAPI = {
  getPrompts: async () => {
    return await apiClient.get('/api/chat/prompts');
  },
  getPrompt: async (promptId) => {
    return await apiClient.get(`/api/chat/prompts/${promptId}`);
  },
  createPrompt: async (promptData) => {
    return await apiClient.post('/api/chat/prompts', promptData);
  },
  updatePrompt: async (promptId, promptData) => {
    return await apiClient.put(`/api/chat/prompts/${promptId}`, promptData);
  },
  deletePrompt: async (promptId) => {
    return await apiClient.delete(`/api/chat/prompts/${promptId}`);
  }
};

// Tools API
export const toolsAPI = {
  getTools: async () => {
    return await apiClient.get('/api/agents/tools');
  },
  getTool: async (toolId) => {
    return await apiClient.get(`/api/agents/tools/${toolId}`);
  },
  createTool: async (toolData) => {
    return await apiClient.post('/api/tools', toolData);
  },
  updateTool: async (toolId, toolData) => {
    return await apiClient.put(`/api/tools/${toolId}`, toolData);
  },
  deleteTool: async (toolId) => {
    return await apiClient.delete(`/api/tools/${toolId}`);
  }
};

export default apiClient;